import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper, 
  Alert, 
  CircularProgress,
  Container
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks/reduxHooks';
import { login, clearError } from '../store/authSlice';
import { COMPANY_NAME } from '../shared/app_config';
import {
  fetchSystemHealth,
  selectDatabaseDown,
  selectCriticalDegraded,
  selectOverallStatus,
} from '../store/systemHealthSlice';
import AppHeader from '../components/AppHeader';
import SystemStatusIndicator from '../components/SystemStatusIndicator';

const LoginPage: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [formErrors, setFormErrors] = useState<{ username?: string; password?: string }>({});
  
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isAuthenticated, loading, error } = useAppSelector(state => state.auth);

  // Health-aware login validation
  const dbDown = useAppSelector(selectDatabaseDown);
  const degraded = useAppSelector(selectCriticalDegraded);
  const overallStatus = useAppSelector(selectOverallStatus);
  
  useEffect(() => {
    // If already authenticated, redirect to dashboard
    if (isAuthenticated) {
      navigate('/');
    }
    
    // Clear any previous errors
    dispatch(clearError());
  }, [isAuthenticated, navigate, dispatch]);

  // Fetch health once on mount (uses 30s cache TTL in slice)
  useEffect(() => {
    dispatch(fetchSystemHealth());
  }, [dispatch]);
  
  const validateForm = () => {
    const errors: { username?: string; password?: string } = {};
    
    if (!username.trim()) {
      errors.username = 'Username is required';
    }
    
    if (!password) {
      errors.password = 'Password is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      await dispatch(login({ username, password }));
    }
  };

  const loginBlocked = dbDown;
  
  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          py: 4,
        }}
      >
        <Paper sx={{ p: 4, width: '100%' }}>
          <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <AppHeader size="large" />
            <Typography variant="h5" component="h2">
              Sign In
            </Typography>
          </Box>

          {/* Database down — block login */}
          {loginBlocked && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Database is unreachable. Login is temporarily unavailable.
            </Alert>
          )}

          {/* Degraded but not blocked — non-blocking warning */}
          {!loginBlocked && degraded && overallStatus !== 'healthy' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              System running in degraded mode. Some features may be unavailable.
            </Alert>
          )}
          
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              label="Username"
              name="username"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              error={!!formErrors.username}
              helperText={formErrors.username}
              disabled={loading || loginBlocked}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={!!formErrors.password}
              helperText={formErrors.password}
              disabled={loading || loginBlocked}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="medium"
              sx={{ mt: 3, mb: 2, py: 1.2 }}
              disabled={loading || loginBlocked}
            >
              {loading ? <CircularProgress size={24} /> : 'Sign In'}
            </Button>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                Contact your department administrator for account access.
              </Typography>
            </Box>
          </Box>
        </Paper>
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <SystemStatusIndicator variant="compact" pollInterval={30000} />
        </Box>
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="caption" color="text.disabled">
            &copy; 2026 {COMPANY_NAME}. All rights reserved.
          </Typography>
        </Box>
      </Box>
    </Container>
  );
};

export default LoginPage;